import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Copy, KeyRound, Plus, Trash2 } from 'lucide-react';
import { ApiKeysAPI, ApiKeyItem, ApiKeyScope, CreatedApiKey } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useCanAccess } from '../hooks/useCanAccess';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Alert, AlertDescription } from './ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

const formatDate = (value: string | null) => (value ? new Date(value).toLocaleString() : '—');

const statusVariant = (status: ApiKeyItem['status']) => (status === 'ACTIVE' ? 'default' : 'secondary');

/**
 * Workgroup-bound API keys. Every key belongs to the workgroup that is active
 * when it is created; switching workgroup shows that workgroup's keys instead.
 * Only roles with `apikeys` (Owner/Admin by default) on a plan that includes
 * API access reach this panel -- see Settings.tsx.
 */
export function ApiKeysPanel() {
  const { activeWorkgroupId, workgroupEpoch, workgroups } = useAuth();
  const canCreate = useCanAccess('apikeys', 'create');
  const canRevoke = useCanAccess('apikeys', 'revoke');
  const workgroupTitle = workgroups.find((w) => w.id === activeWorkgroupId)?.title;

  const [keys, setKeys] = useState<ApiKeyItem[]>([]);
  const [scopes, setScopes] = useState<ApiKeyScope[]>([]);
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [expiresOn, setExpiresOn] = useState(''); // yyyy-mm-dd, optional
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<CreatedApiKey | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ApiKeyItem | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, scopesRes] = await Promise.all([ApiKeysAPI.list(), ApiKeysAPI.scopes()]);
      setKeys(listRes.data ?? []);
      setScopes(scopesRes.data ?? []);
    } catch {
      // Surfaced by the global toast in apiClient.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, activeWorkgroupId, workgroupEpoch]);

  const resetForm = () => {
    setName('');
    setSelectedScopes([]);
    setExpiresOn('');
  };

  const todayIso = new Date().toISOString().slice(0, 10);
  const expiryInvalid = !!expiresOn && expiresOn <= todayIso;
  const canSubmit = name.trim().length > 0 && selectedScopes.length > 0 && !expiryInvalid && !creating;

  const handleCreate = async () => {
    setCreating(true);
    try {
      const res = await ApiKeysAPI.create({
        name: name.trim(),
        scopes: selectedScopes,
        // Expiry is optional: omit it entirely for a key that never expires. End of the chosen day, local time.
        ...(expiresOn ? { expiresOn: new Date(`${expiresOn}T23:59:59`).toISOString() } : {}),
      });
      setCreateOpen(false);
      resetForm();
      if (res.data) setCreated(res.data);
      await load();
    } catch {
      // Surfaced by the global toast in apiClient.
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    try {
      await ApiKeysAPI.revoke(revokeTarget.id);
      toast.success(`Revoked "${revokeTarget.name}"`);
      setRevokeTarget(null);
      await load();
    } catch {
      setRevokeTarget(null);
    }
  };

  const toggleScope = (scope: string, checked: boolean) =>
    setSelectedScopes((prev) => (checked ? [...prev, scope] : prev.filter((s) => s !== scope)));

  const copyKey = async () => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.key);
      toast.success('API key copied');
    } catch {
      toast.error('Could not copy — select and copy the key manually');
    }
  };

  return (
    <>
      <Card className="bg-card shadow-sm rounded-lg border-0">
        <CardHeader className="pb-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="flex items-center space-x-2">
                <KeyRound className="w-5 h-5 text-primary" />
                <CardTitle>API Keys</CardTitle>
              </div>
              <CardDescription>
                Call the Orbit8 API from your own systems with a key
                {workgroupTitle ? <> bound to <strong>{workgroupTitle}</strong></> : null}. A key only ever accesses this workgroup.
              </CardDescription>
            </div>
            {canCreate && (
              <Button onClick={() => setCreateOpen(true)}>
                <Plus className="w-4 h-4 mr-1.5" />
                Create key
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : keys.length === 0 ? (
            <p className="text-sm text-muted-foreground">No API keys yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Key</TableHead>
                  <TableHead>Scopes</TableHead>
                  <TableHead>Created by</TableHead>
                  <TableHead>Last used</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {keys.map((k) => (
                  <TableRow key={k.id}>
                    <TableCell className="font-medium">{k.name}</TableCell>
                    <TableCell className="font-mono text-xs">{k.prefix}…</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {k.scopes.map((s) => (
                          <Badge key={s} variant="outline">{s}</Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>{k.createdby.name || k.createdby.email}</TableCell>
                    <TableCell>{formatDate(k.lastusedon)}</TableCell>
                    <TableCell>{k.expireson ? formatDate(k.expireson) : 'Never'}</TableCell>
                    <TableCell>
                      <Badge variant={statusVariant(k.status)}>{k.status}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {canRevoke && k.status !== 'REVOKED' && (
                        <Button variant="ghost" size="icon" aria-label={`Revoke ${k.name}`} onClick={() => setRevokeTarget(k)}>
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open);
          if (!open) resetForm();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create API key</DialogTitle>
            <DialogDescription>
              The key will be bound to {workgroupTitle ? <strong>{workgroupTitle}</strong> : 'the current workgroup'} and act with your role there.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="api-key-name">Name</Label>
              <Input id="api-key-name" value={name} maxLength={100} placeholder="e.g. CI pipeline" onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Scopes</Label>
              {scopes.map((s) => (
                <label key={s.scope} className="flex items-start gap-2 text-sm">
                  <Checkbox checked={selectedScopes.includes(s.scope)} onCheckedChange={(c) => toggleScope(s.scope, c === true)} />
                  <span>
                    <span className="font-mono">{s.scope}</span>
                    <span className="block text-muted-foreground">{s.description}</span>
                  </span>
                </label>
              ))}
            </div>
            <div className="space-y-2">
              <Label htmlFor="api-key-expiry">Expiry (optional)</Label>
              <Input id="api-key-expiry" type="date" min={todayIso} value={expiresOn} onChange={(e) => setExpiresOn(e.target.value)} />
              <p className="text-xs text-muted-foreground">Leave empty for a key that never expires.</p>
              {expiryInvalid && <p className="text-xs text-destructive">Expiry must be a future date.</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={!canSubmit}>{creating ? 'Creating…' : 'Create key'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!created} onOpenChange={(open) => !open && setCreated(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Your new API key</DialogTitle>
            <DialogDescription>Copy it now. For security it is shown only once and cannot be retrieved later.</DialogDescription>
          </DialogHeader>
          <Alert>
            <AlertDescription className="font-mono text-xs break-all select-all">{created?.key}</AlertDescription>
          </Alert>
          <DialogFooter>
            <Button variant="outline" onClick={copyKey}>
              <Copy className="w-4 h-4 mr-1.5" />
              Copy
            </Button>
            <Button onClick={() => setCreated(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!revokeTarget} onOpenChange={(open) => !open && setRevokeTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke "{revokeTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>Any system using this key will immediately lose access. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRevoke}>Revoke</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default ApiKeysPanel;
